import {Evaluable, evaluateWhenFunction} from "../evaluable";
import {
    AssertionLogicFunction,
    assertType,
    NarrowedNonUnionTypeAssertController
} from "../type-assertion/type-assertion";

export type HideableValueHiddenState<V> = {
    __hidden: true;
    value: V;
};

export type HideableValue<V> = V | HideableValueHiddenState<V>;

export namespace HideableValue {
    export type ResolveValueType<T> = T extends HideableValue<infer U> ? U : T;
}

export function hideableValueIsVisibleAndNotEqualValue<V> (value: any, comparedValue: V) : value is V {
    return !hideableValueIsHidden(value) && hideableValueGetValue(value) !== comparedValue;
}

export function hideableValueIsVisible<V> (value: HideableValue<V>) : value is V {
    return !hideableValueIsHidden(value);
}

export function hideableValueIsHidden<V> (value: any) : value is HideableValueHiddenState<V> {
    return typeof value === 'object' && value !== null && (value as HideableValueHiddenState<V>).__hidden === true;
}

export function hideableValueConvert<V, R> (hideableValue: HideableValue<V>, convertFunction: (isVisible: boolean, value: V) => R) : R {
    return convertFunction(hideableValueIsVisible(hideableValue), hideableValueGetValue(hideableValue));
}

export function hideableValueGetValue<V> (value: HideableValue<V>) : V {
    if (hideableValueIsHidden<V>(value)) {
        return value.value;
    } else {
        return value;
    }
}

export function hideableValueModifyValue<V> (
    hideableValue: HideableValue<V>,
    modificationFunction: Evaluable<(value: V) => V>
) : HideableValue<V> {
    const value = hideableValueGetValue(hideableValue);

    const modifiedValue = evaluateWhenFunction(modificationFunction, value);

    if (hideableValueIsHidden(hideableValue)) {
        return hideableValueCreateHidden(modifiedValue);
    } else {
        return modifiedValue;
    }
}

export function hideableValueGetVisibleValue<V, H> (value: HideableValue<V>, hiddenValue: H) : V | H {
    if (hideableValueIsHidden<V>(value)) {
        return hiddenValue;
    } else {
        return value;
    }
}

export function hideableValueCreateHidden<V> (value: V) : HideableValueHiddenState<V> {
    return {
        __hidden: true,
        value: value
    }
}

export function hideableValueCreate<V> (value: V, isHidden: boolean) {
    if (isHidden) {
        return hideableValueCreateHidden(value);
    } else {
        return value;
    }
}

export function hideableValueEnsureHidden<V> (value: HideableValue<V>) : HideableValueHiddenState<V> {
    if (hideableValueIsHidden(value)) {
        return value;
    } else {
        return hideableValueCreateHidden(value);
    }
}

export function hideableValueToggleVisible<V> (
    value: HideableValue<V>,
    isVisible: Evaluable<(isVisible: boolean) => boolean>
) : HideableValue<V> {
    return hideableValueToggleHidden(value, wasHidden => !evaluateWhenFunction(isVisible, !wasHidden))
}

export function hideableValueToggleHidden<V> (
    value: HideableValue<V>,
    isHidden: Evaluable<(isHidden: boolean) => boolean>
) : HideableValue<V> {
    const wasHidden = hideableValueIsHidden(value);

    const evaluatedIsHidden = evaluateWhenFunction(isHidden, wasHidden);

    if (wasHidden !== evaluatedIsHidden) {
        return wasHidden ? value.value : hideableValueCreateHidden(value);
    } else {
        return value;
    }
}

export function assertHideableValue<V> (hideableValue: HideableValue<V>, assertionLogic: AssertionLogicFunction<V>) : asserts hideableValue is HideableValue<V>;
export function assertHideableValue<V> (hideableValue: unknown, assertionLogic: AssertionLogicFunction<V>) : asserts hideableValue is HideableValue<V>;
export function assertHideableValue<V> (hideableValue: unknown, assertionLogic: AssertionLogicFunction<V>) : asserts hideableValue is HideableValue<V> {
    assertType<HideableValue<V>>(hideableValue, assert => {
        (assert as any).isUnion(
            assertionLogic,
            (assert: NarrowedNonUnionTypeAssertController<HideableValueHiddenState<V>>) => assert.isObject({
                __hidden: assert => assert.isOneOfValues([true]),
                value: assertionLogic
            })
        )
    })
}